import IconRight from '@iconify/icons-material-symbols/chevron-right';
import type { HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Icon } from '#pie/components/icon/Icon';
import { useProtectedMenu } from '#pie/hooks/useProtectedMenu';
import { cn, tv } from '#pie/utils/TailwindUtils';
import Logo from '../../../assets/logo.svg?react';
import { SidebarMenu } from './menu/SidebarMenu';

interface SidebarProps extends HTMLAttributes<HTMLDivElement> {
  isOpen?: boolean;
  setIsOpen?: (value: boolean) => void;
}

export const Sidebar = ({ isOpen, setIsOpen, className }: SidebarProps) => {
  const { t } = useTranslation();
  const s = styles({ isOpen });

  const { bottomMenu, mainMenu } = useProtectedMenu();
  return (
    <div id="sidebar" className={cn(s.base(), className)}>
      <Link
        data-testid="logo"
        to="/"
        className={s.logo()}
        title={t('home.page_title')}
      >
        <Logo className="max-w-17" />
      </Link>

      <button
        aria-controls="sidebar"
        aria-expanded={isOpen ? 'true' : 'false'}
        className={s.toggle()}
        type="button"
        title={isOpen ? 'Sluit sidebar' : 'Open sidebar'}
        onClick={() => setIsOpen?.(!isOpen)}
      >
        <Icon icon={IconRight} size={12} />
      </button>

      <SidebarMenu menu={mainMenu} isOpen={isOpen} />
      <SidebarMenu menu={bottomMenu} isOpen={isOpen} className="mt-auto mb-2" />
    </div>
  );
};

export const styles = tv({
  slots: {
    base: 'relative h-full bg-surface-bold-atlantic flex-shrink-0 z-10 flex flex-col',
    logo: 'min-h-[73px] h-[73px] flex text-content-reversed items-center focus:outline-hidden',
    toggle:
      'z-10 bg-action-primary-hover absolute right-0 top-[84px] grid h-12 w-3 translate-x-3 place-items-center text-content-reversed rounded-r-sm',
  },

  variants: {
    isOpen: {
      false: {
        base: 'w-[64px]',
        logo: 'mx-4',
      },
      true: {
        base: 'w-[240px]',
        logo: 'mx-4',
        toggle: 'rotate-180 rounded-l-sm rounded-r-none',
      },
    },
  },
});
